/**
 * Alta y edición de un centro.
 *
 * Es el formulario con más consecuencia legal del ERP: **los campos de
 * cumplimiento deciden si el centro está sujeto a inspección periódica por
 * un organismo de control** (art. 22 RIPCI). Por eso cada uno lleva su
 * ayuda contextual explicando la consecuencia, no el formato: quien
 * rellena «superficie construida» tiene que saber que sin ella la
 * inspección se exige por prudencia.
 *
 * Al guardar, la API devuelve el resultado del motor y se enseña en el
 * acto: «este centro necesita inspección cada 10 años» es una información
 * que el comercial necesita antes de irse del cliente.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { CheckField, FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { isoDate, maxLength, nonNegative, required, type FieldRules } from '../../forms/form-state.js';

interface Compliance {
  required: boolean;
  intervalYears: number | null;
  reason: string;
  articleRef: string;
}

interface SiteDetail {
  id: string;
  code: string;
  name: string;
  customer_id: string;
  main_use: string;
  built_area_m2: string | null;
  has_high_risk_area: boolean;
  has_storage_area: boolean;
  is_industrial_establishment: boolean;
  fire_load_qs_mj_m2: string | null;
  construction_date: string | null;
  only_exempt_equipment: boolean;
  access_instructions: string | null;
  keys_info: string | null;
  risks: string | null;
  notes: string | null;
  version: number;
}

const USES = [
  { value: 'RESIDENCIAL_VIVIENDA', label: 'Residencial vivienda' },
  { value: 'ADMINISTRATIVO', label: 'Administrativo' },
  { value: 'DOCENTE', label: 'Docente' },
  { value: 'COMERCIAL', label: 'Comercial' },
  { value: 'PUBLICA_CONCURRENCIA', label: 'Pública concurrencia' },
  { value: 'APARCAMIENTO', label: 'Aparcamiento' },
  { value: 'INDUSTRIAL', label: 'Industrial' },
  { value: 'HOSPITALARIO', label: 'Hospitalario' },
  { value: 'OTROS', label: 'Otros' },
];

const RULES: FieldRules = {
  code: [required('El código'), maxLength(32)],
  name: [required('El nombre'), maxLength(255)],
  mainUse: [required('El uso principal')],
  builtAreaM2: [nonNegative()],
  fireLoadQsMjM2: [nonNegative()],
  constructionDate: [isoDate()],
};

const NULLABLE = ['builtAreaM2', 'fireLoadQsMjM2', 'constructionDate', 'branchId'];

export function SiteFormScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = id !== undefined && id !== 'nuevo';

  const [detail, setDetail] = useState<SiteDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [compliance, setCompliance] = useState<Compliance | null>(null);
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;

    api
      .get<SiteDetail>(`/sites/${id}`)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el centro.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, editing, id]);

  const form = useForm<{ compliance: Compliance }>({
    mode: editing ? 'edit' : 'create',
    rules: RULES,
    nullable: NULLABLE,
    initial: {
      code: detail?.code ?? '',
      name: detail?.name ?? '',
      mainUse: detail?.main_use ?? 'OTROS',
      builtAreaM2: detail?.built_area_m2 ?? '',
      hasHighRiskArea: detail?.has_high_risk_area ?? false,
      hasStorageArea: detail?.has_storage_area ?? false,
      isIndustrialEstablishment: detail?.is_industrial_establishment ?? false,
      fireLoadQsMjM2: detail?.fire_load_qs_mj_m2 ?? '',
      constructionDate: detail?.construction_date ?? '',
      onlyExemptEquipment: detail?.only_exempt_equipment ?? false,
      accessInstructions: detail?.access_instructions ?? '',
      keysInfo: detail?.keys_info ?? '',
      risks: detail?.risks ?? '',
      notes: detail?.notes ?? '',
    },
    onSubmit: async (body, client) => {
      // Los números viajan como números, no como el texto del `<input>`:
      // la API los valida con Zod y una cadena se rechazaría.
      const payload: Record<string, unknown> = { ...body };
      for (const key of ['builtAreaM2', 'fireLoadQsMjM2']) {
        if (typeof payload[key] === 'string' && payload[key] !== '') {
          payload[key] = Number(payload[key]);
        }
      }

      if (editing) {
        // `expectedVersion` es lo que convierte «guardar» en «guardar si
        // nadie lo ha tocado». Sin él, el último en pulsar gana en
        // silencio.
        payload['expectedVersion'] = detail?.version;
        return client.request<{ compliance: Compliance }>(`/sites/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      }

      payload['customerId'] = customerId;
      return client.post<{ id: string; compliance: Compliance }>('/sites', payload);
    },
    onSaved: (saved) => {
      setCompliance(saved.compliance);
    },
  });

  if (loadError !== null) {
    return (
      <section className="screen">
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{editing ? 'Editar centro' : 'Nuevo centro'}</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {form.formError !== null && (
        <p className={`screen__error${form.versionConflict ? ' screen__error--conflict' : ''}`} role="alert">
          {form.formError}
          {form.versionConflict && (
            <button className="table__link" type="button" onClick={() => window.location.reload()}>
              Recargar
            </button>
          )}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      {compliance && (
        <p className={`screen__note${compliance.required ? ' screen__note--strong' : ''}`}>
          {compliance.required
            ? `Este centro necesita inspección periódica cada ${compliance.intervalYears ?? '—'} años.`
            : 'Este centro no está sujeto a inspección periódica.'}{' '}
          {compliance.reason} <em>({compliance.articleRef})</em>
        </p>
      )}

      <form className="form" onSubmit={(e) => void form.submit(e)} noValidate>
        <FormSection title="Identificación">
          {!editing && (
            <EntityPicker
              label="Cliente"
              entity="CUSTOMER"
              value={customerId}
              onChange={setCustomerId}
              required
              help="Se busca por razón social, código o NIF."
            />
          )}
          <TextField
            label="Código"
            value={String(form.values['code'] ?? '')}
            onChange={(v) => form.set('code', v)}
            onBlur={() => form.blur('code')}
            error={form.errors['code']}
            required
            maxLength={32}
          />
          <TextField
            label="Nombre del centro"
            value={String(form.values['name'] ?? '')}
            onChange={(v) => form.set('name', v)}
            onBlur={() => form.blur('name')}
            error={form.errors['name']}
            required
            maxLength={255}
          />
        </FormSection>

        <FormSection
          title="Cumplimiento"
          description={
            <>
              Estos campos <strong>deciden si el centro está sujeto a inspección periódica</strong> por
              un organismo de control (art. 22 RIPCI). No son descriptivos: el sistema calcula con
              ellos, así que un dato aproximado produce una obligación aproximada.
            </>
          }
        >
          <SelectField
            label="Uso principal"
            value={String(form.values['mainUse'] ?? '')}
            onChange={(v) => form.set('mainUse', v)}
            onBlur={() => form.blur('mainUse')}
            error={form.errors['mainUse']}
            options={USES}
            required
            help="Determina el umbral de exención del art. 22.2 RIPCI."
          />
          <TextField
            label="Superficie construida (m²)"
            type="number"
            step="0.01"
            value={String(form.values['builtAreaM2'] ?? '')}
            onChange={(v) => form.set('builtAreaM2', v)}
            onBlur={() => form.blur('builtAreaM2')}
            error={form.errors['builtAreaM2']}
            help="Sin ella la inspección se exige por prudencia: el sistema no supone que el centro es pequeño."
          />
          <CheckField
            label="Hay zonas o locales de riesgo especial alto"
            checked={form.values['hasHighRiskArea'] === true}
            onChange={(v) => form.set('hasHighRiskArea', v)}
            help="Anula cualquier exención por superficie o uso."
          />
          <CheckField
            label="Hay zonas de uso almacén"
            checked={form.values['hasStorageArea'] === true}
            onChange={(v) => form.set('hasStorageArea', v)}
            help="Anula cualquier exención."
          />
          <CheckField
            label="Establecimiento industrial"
            checked={form.values['isIndustrialEstablishment'] === true}
            onChange={(v) => form.set('isIndustrialEstablishment', v)}
            help="Pasa al RD 164/2025, con inspección cada 5 años y otro régimen de obligaciones."
          />
          <TextField
            label="Carga de fuego Qs (MJ/m²)"
            type="number"
            step="0.01"
            value={String(form.values['fireLoadQsMjM2'] ?? '')}
            onChange={(v) => form.set('fireLoadQsMjM2', v)}
            onBlur={() => form.blur('fireLoadQsMjM2')}
            error={form.errors['fireLoadQsMjM2']}
            help="Sólo para establecimientos industriales: determina el nivel de riesgo intrínseco."
          />
          <TextField
            label="Fecha de construcción"
            type="date"
            value={String(form.values['constructionDate'] ?? '')}
            onChange={(v) => form.set('constructionDate', v)}
            onBlur={() => form.blur('constructionDate')}
            error={form.errors['constructionDate']}
            help="Determina el régimen industrial aplicable (DT 1.ª RD 164/2025). No es la fecha de instalación de la PCI."
          />
          <CheckField
            label="Sólo extintores, mantas o planos de evacuación"
            checked={form.values['onlyExemptEquipment'] === true}
            onChange={(v) => form.set('onlyExemptEquipment', v)}
            help="Supuesto de exención del art. 20.2. Márquelo sólo si es literalmente cierto."
          />
        </FormSection>

        <FormSection title="Acceso y notas">
          <TextArea
            label="Instrucciones de acceso"
            value={String(form.values['accessInstructions'] ?? '')}
            onChange={(v) => form.set('accessInstructions', v)}
            help="Lo que el técnico necesita saber para entrar sin llamar a la oficina."
          />
          <TextField
            label="Llaves"
            value={String(form.values['keysInfo'] ?? '')}
            onChange={(v) => form.set('keysInfo', v)}
          />
          <TextArea
            label="Riesgos del centro"
            value={String(form.values['risks'] ?? '')}
            onChange={(v) => form.set('risks', v)}
          />
          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear centro'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate(editing ? `/centros/${id}` : '/centros');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
